import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NavBar from '../components/nav';
import { Footer } from '../components/footer';
import PageBanner from '../components/PageBanner';
import { Home, Mail, Phone, MapPin, Send, Instagram } from 'lucide-react';
import { sendContactMessage } from '../services/contactApi';
import TurnstileWidget from '../components/TurnstileWidget';

export default function Contact() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await sendContactMessage({ ...formData, turnstile_token: turnstileToken });
      setSubmitted(true);

      // Réinitialiser le formulaire après 3 secondes
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
        setSubmitted(false);
      }, 3000);
    } catch (err) {
      console.error('Erreur lors de l\'envoi du message:', err);
      setError(err.message || t('contact.genericSendError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <NavBar />
      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-4">
          <nav className="flex items-center space-x-2 text-sm text-gray-900 font-normal mb-6">
            <Link to="/" className="flex items-center hover:text-green-600 transition-colors">
              <Home className="w-4 h-4 mr-1" />
              {t('contact.home')}
            </Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900 font-medium">{t('contact.breadcrumb')}</span>
          </nav>
        </div>

        <PageBanner />

        {/* Header Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('contact.title')}
            </h1>
            <p className="text-xl text-gray-900 font-normal max-w-3xl mx-auto">
              {t('contact.subtitle')}
            </p>
          </div>

          {/* Main Content */}
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

              {/* Contact Information */}
              <div className="space-y-6">
                <div className="bg-white rounded-2xl p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    {t('contact.contactInfoTitle')}
                  </h2>

                  <div className="space-y-5">
                    {/* Address */}
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="text-gray-500" size={18} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-0.5 text-sm">{t('contact.address')}</h3>
                        <p className="text-gray-900 font-normal text-sm leading-relaxed">
                          Aristotelessteig 10<br />
                          10318 Berlin, {t('contact.germany')}
                        </p>
                      </div>
                    </div>

                    {/* Email */}
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="text-gray-500" size={18} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-0.5 text-sm">{t('contact.email')}</h3>
                        <a href="mailto:contact@namke.app" className="text-sm text-gray-900 font-normal hover:text-gray-900 transition-colors">
                          contact@namke.app
                        </a>
                      </div>
                    </div>

                    {/* Phone */}
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="text-gray-500" size={18} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-0.5 text-sm">{t('contact.phone')}</h3>
                        <a href="tel:+4917686660296" className="text-sm text-gray-900 font-normal hover:text-gray-900 transition-colors">
                          +49 176 86660296
                        </a>
                      </div>
                    </div>

                    {/* Instagram */}
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Instagram className="text-gray-500" size={18} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-0.5 text-sm">{t('contact.instagram')}</h3>
                        <a
                          href="https://instagram.com/namke_app"
                          target="_blank" rel="noopener noreferrer"
                          className="text-sm text-gray-900 font-normal hover:text-gray-900 transition-colors"
                        >
                          @namke_app
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Hours */}
                <div className="bg-white rounded-2xl p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    {t('contact.hoursTitle')}
                  </h2>
                  <div className="space-y-2 text-gray-900 font-normal">
                    <p><span className="font-semibold">{t('contact.mondayFriday')}</span> 9h00 - 18h00</p>
                    <p><span className="font-semibold">{t('contact.saturday')}</span> 10h00 - 16h00</p>
                    <p><span className="font-semibold">{t('contact.sunday')}</span> {t('contact.closed')}</p>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-white rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  {t('contact.sendMessageTitle')}
                </h2>

                {submitted && (
                  <div className="mb-6 form-message form-message--success">
                    {t('contact.successMessage')}
                  </div>
                )}

                {error && (
                  <div className="mb-6 form-message form-message--error">
                    ✗ {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.fullName')}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                      placeholder={t('contact.namePlaceholder')}
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.emailLabel')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                      placeholder={t('contact.emailPlaceholder')}
                    />
                  </div>

                  {/* Subject */}
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.subject')}
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                      placeholder={t('contact.subjectPlaceholder')}
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.message')}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors resize-none"
                      placeholder={t('contact.messagePlaceholder')}
                    />
                  </div>

                  <TurnstileWidget onVerify={setTurnstileToken} onExpire={() => setTurnstileToken(null)} />

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading || (!!process.env.REACT_APP_TURNSTILE_SITE_KEY && !turnstileToken)}
                    className="w-full bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center space-x-2 font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>{t('contact.sending')}</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>{t('contact.sendMessage')}</span>
                      </>
                    )}
                  </button>
                </form>
              </div>

            </div>

            {/* FAQ Section */}
            <div className="mt-12 bg-white rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {t('contact.faqTitle')}
              </h2>

              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {t('contact.faq1Q')}
                  </h3>
                  <p className="text-gray-900 font-normal">
                    {t('contact.faq1A')}
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {t('contact.faq2Q')}
                  </h3>
                  <p className="text-gray-900 font-normal">
                    {t('contact.faq2A')}
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {t('contact.faq3Q')}
                  </h3>
                  <p className="text-gray-900 font-normal">
                    {t('contact.faq3A')}
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {t('contact.faq4Q')}
                  </h3>
                  <p className="text-gray-900 font-normal">
                    {t('contact.faq4A')}
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {t('contact.faq5Q')}
                  </h3>
                  <p className="text-gray-900 font-normal">
                    {t('contact.faq5A')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
